/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    TextField,
    List,
    ListItem,
    styled,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ListItemButton } from '@mui/material';
import api from 'api';
import { Box } from '@mui/system';
import type { CountryCode } from 'types/countries';
import { marketFields } from 'utils/countries';
import { Profession } from './WorkExperienceV2';

interface Props {
    open: boolean;
    handleClose: () => void;
    handleUpdate: (index: number, field: string, value: string | null | number) => void;
    handleRecommendedJobs: any;
    index: number;
    country: string | null;
    jobTitle: any;
    options: Profession[];
    countryCode: CountryCode;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
        overflowY: 'auto',
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
        display: 'flex',
        justifyContent: 'space-between',
    },
    border: '1px solid #4a52bb',
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: 'none', // Remove individual borders
    backgroundColor: 'white', // Set background to white
    fontSize: '0.875rem', // Standard font size
    width: '100%', // Full width to fill the container
    display: 'flex',
    alignItems: 'center',
    '&.selected': {
        backgroundColor: '#f4f0ff', // Light purple background for selected
        color: '#5e35b1', // Purple text color for selected
    },
    '&:hover': {
        backgroundColor: '#f8f8f8', // Light grey on hover
    },
}));

const StyledList = styled(List)(({ theme }) => ({
    width: '100%',
    bgcolor: 'background.paper',
    overflowX: 'hidden',
    overflowY: 'auto',
    maxHeight: 300,
    border: '1px solid #ccc',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 2px 5px rgba(0,0,0,0.1)',
    display: 'block',
}));

export default function AIRecommendedFieldOfStudy({
    open,
    handleClose,
    handleUpdate,
    index,
    jobTitle,
    countryCode,
}: Props) {
    const { t } = useTranslation();
    const [description, setDescription] = useState('');
    const [suggestedTitles, setSuggestedTitles] = useState<any[]>([]);

    const fieldOfStudyIdField = marketFields(countryCode).fieldOfStudyIdKey;

    const handleListItemClick = (id: any) => {
        handleUpdate(index, fieldOfStudyIdField, id);
        setDescription('');
    };

    const handleFetchFields = async () => {
        if (description === '') return;
        try {
            const response = await api.fieldsOfStudy_recommendation.getRecommendedFieldsOfStudy(
                countryCode,
                description
            );
            const titles = response.data.fields_of_study.map((item: any) => ({
                id: item.id,
                name: item.name,
            }));
            setSuggestedTitles(titles);
        } catch (e) {
            console.error(e);
            setSuggestedTitles([]);
            alert('There was some error. Please try again.');
        }
    };

    return (
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={open}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {t('aiAssistant')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers sx={{ overflow: 'hidden', width: '320px' }}>
                <Box>
                    <TextField
                        label={t('description')}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        placeholder={t('descriptionPlaceholder')}
                        multiline
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </Box>
                <Button
                    variant="outlined"
                    onClick={handleFetchFields}
                    fullWidth
                    sx={{
                        marginTop: 2,
                        color: '#4a52bb',
                        borderColor: '#4a52bb',
                        backgroundColor: 'white',
                        '&:hover': {
                            backgroundColor: 'white',
                            borderColor: '#4a52bb',
                        },
                        borderRadius: '3px',
                        textTransform: 'none',
                    }}
                >
                    ✨ {t('getStudyFields')}
                </Button>

                {suggestedTitles.length > 0 && (
                    <StyledList>
                        {suggestedTitles.map((item, index) => (
                            <ListItem
                                disablePadding
                                key={index}
                                selected={item?.name === jobTitle?.name}
                            >
                                <StyledListItemButton
                                    onClick={() => {
                                        handleListItemClick(item?.id);
                                        handleClose();
                                    }}
                                    className={item?.name === jobTitle?.name ? 'selected' : ''}
                                >
                                    {item?.name}
                                </StyledListItemButton>
                            </ListItem>
                        ))}
                    </StyledList>
                )}
            </DialogContent>
        </BootstrapDialog>
    );
}
